import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
    Card,
    List,
    Button,
    Badge,
    Typography,
    Space,
    Empty,
    Spin,
    Tag,
    Divider,
    message,
    Popconfirm,
} from 'antd';
import {
    BellOutlined,
    DeleteOutlined,
    CheckOutlined,
    ReloadOutlined,
    EyeOutlined,
    MailOutlined,
    MessageOutlined,
    CreditCardOutlined,
    DollarOutlined,
    MoneyCollectOutlined,
    StarOutlined,
    CalendarOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification } from '../../store/slices/notificationSlice';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Notifications = () => {
    const dispatch = useAppDispatch();
    const { user, loading: authLoading } = useAuth();
    const { notifications, unreadCount, loading, error } = useAppSelector(state => state.notifications);
    const [selectedNotifications, setSelectedNotifications] = useState([]);

    // Always call hooks at the top level
    useEffect(() => {
        if (!authLoading && user && user.user_type !== 'admin') {
            dispatch(fetchNotifications());
        }
    }, [dispatch, authLoading, user]);

    // Wait for auth to load
    if (authLoading) {
        return <div>Loading...</div>;
    }

    // If not authenticated, redirect to login
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // If user is admin, redirect to admin notifications
    if (user.user_type === 'admin') {
        return <Navigate to="/admin/notifications" replace />;
    }

    const handleMarkAsRead = async (notificationId) => {
        try {
            await dispatch(markNotificationAsRead(notificationId)).unwrap();
            message.success('Notification marked as read');
        } catch (error) {
            message.error('Failed to mark notification as read');
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await dispatch(markAllNotificationsAsRead()).unwrap();
            message.success('All notifications marked as read');
        } catch (error) {
            message.error('Failed to mark all notifications as read');
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await dispatch(deleteNotification(notificationId)).unwrap();
            message.success('Notification deleted');
        } catch (error) {
            message.error('Failed to delete notification');
        }
    };

    const handleRefresh = () => {
        dispatch(fetchNotifications());
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'booking':
                return <CalendarOutlined style={{ color: '#1890ff' }} />;
            case 'quote':
                return <FileTextOutlined style={{ color: '#52c41a' }} />;
            case 'payment':
                return <DollarOutlined style={{ color: '#722ed1' }} />;
            case 'milestone':
                return <CreditCardOutlined style={{ color: '#13c2c2' }} />;
            case 'advance':
                return <MoneyCollectOutlined style={{ color: '#fa8c16' }} />;
            case 'review':
                return <StarOutlined style={{ color: '#fadb14' }} />;
            case 'email':
                return <MailOutlined style={{ color: '#1890ff' }} />;
            case 'message':
                return <MessageOutlined style={{ color: '#52c41a' }} />;
            default:
                return <BellOutlined style={{ color: '#8c8c8c' }} />;
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'booking':
                return 'blue';
            case 'quote':
                return 'green';
            case 'payment':
                return 'purple';
            case 'milestone':
                return 'cyan';
            case 'advance':
                return 'orange';
            case 'review':
                return 'gold';
            case 'email':
                return 'blue';
            case 'message':
                return 'green';
            default:
                return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent':
                return 'red';
            case 'high':
                return 'orange';
            case 'normal':
                return 'blue';
            case 'low':
                return 'green';
            default:
                return 'default';
        }
    };

    if (loading && notifications.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Card>
                    <Empty
                        description="Failed to load notifications"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                        <Button type="primary" onClick={handleRefresh}>
                            Try Again
                        </Button>
                    </Empty>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4">
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <Title level={2} className="!mb-2">
                                Notifications
                            </Title>
                            <Text type="secondary">
                                Stay updated with your latest activities
                            </Text>
                        </div>
                        <Space>
                            <Badge count={unreadCount} size="small">
                                <Button
                                    icon={<BellOutlined />}
                                    onClick={handleRefresh}
                                    loading={loading}
                                >
                                    Refresh
                                </Button>
                            </Badge>
                            {unreadCount > 0 && (
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    onClick={handleMarkAllAsRead}
                                >
                                    Mark All as Read
                                </Button>
                            )}
                        </Space>
                    </div>
                </div>

                {notifications.length === 0 ? (
                    <Card>
                        <Empty
                            description="No notifications yet"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <Text type="secondary">
                                You'll see your notifications here when they arrive
                            </Text>
                        </Empty>
                    </Card>
                ) : (
                    <Card>
                        <List
                            dataSource={notifications}
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} notifications`,
                            }}
                            renderItem={(notification) => (
                                <List.Item
                                    key={notification.id}
                                    className={`notification-item ${!notification.read_at ? 'unread' : ''}`}
                                    style={{
                                        backgroundColor: !notification.read_at ? '#f6ffed' : 'transparent',
                                        borderLeft: !notification.read_at ? '4px solid #52c41a' : '4px solid transparent',
                                        padding: '16px',
                                        marginBottom: '8px',
                                        borderRadius: '6px',
                                    }}
                                    actions={[
                                        !notification.read_at && (
                                            <Button
                                                key="read"
                                                type="text"
                                                size="small"
                                                icon={<CheckOutlined />}
                                                onClick={() => handleMarkAsRead(notification.id)}
                                            >
                                                Mark as Read
                                            </Button>
                                        ),
                                        <Popconfirm
                                            key="delete"
                                            title="Delete this notification?"
                                            description="This action cannot be undone."
                                            onConfirm={() => handleDelete(notification.id)}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            >
                                                Delete
                                            </Button>
                                        </Popconfirm>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                        }
                                        title={
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <Text strong={!notification.read_at}>
                                                        {notification.title}
                                                    </Text>
                                                    {!notification.read_at && (
                                                        <Badge dot color="#52c41a" />
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Tag color={getNotificationColor(notification.type)}>
                                                        {notification.type?.toUpperCase() || 'NOTIFICATION'}
                                                    </Tag>
                                                    {notification.priority && (
                                                        <Tag color={getPriorityColor(notification.priority)}>
                                                            {notification.priority.toUpperCase()}
                                                        </Tag>
                                                    )}
                                                </div>
                                            </div>
                                        }
                                        description={
                                            <div>
                                                <Text type="secondary">
                                                    {notification.message}
                                                </Text>
                                                <Divider style={{ margin: '8px 0' }} />
                                                <div className="flex justify-between items-center text-xs text-gray-500">
                                                    <Text type="secondary">
                                                        {dayjs(notification.created_at).format('MMM DD, YYYY [at] h:mm A')}
                                                    </Text>
                                                    {notification.read_at && (
                                                        <Text type="secondary">
                                                            Read {dayjs(notification.read_at).fromNow()}
                                                        </Text>
                                                    )}
                                                </div>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                )}
            </div>
        </div>
    );
};

export default Notifications;
